import { useEffect, useRef, useState } from "react";

export default function Contact() {
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("sending");
    const form = e.currentTarget;
    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value.toString());
    });
    try {
      const res = await fetch("https://readdy.ai/api/form/d718erghb0rqicn68cl0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" ref={ref} className="relative py-28 bg-[#07070f] overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#e879f9] to-transparent opacity-30" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#e879f9]/3 blur-3xl pointer-events-none" />

      <div className="relative max-w-3xl mx-auto px-6">
        {/* Header */}
        <div
          className={`flex flex-col items-center text-center mb-14 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="font-mono-tech text-xs text-[#e879f9] uppercase tracking-widest mb-3">// get in touch</span>
          <h2 className="font-orbitron text-3xl md:text-5xl font-black uppercase text-white mb-2">
            Contact <span className="neon-text-fuchsia">Me</span>
          </h2>
          <p className="font-rajdhani text-base text-slate-400 max-w-lg">
            Collabs, sponsorships, fan mail — drop a message. I read everything personally and reply to most things.
          </p>
        </div>

        {/* Contact Info row */}
        <div
          className={`grid grid-cols-1 md:grid-cols-3 gap-4 mb-10 transition-all duration-700 delay-100 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          {[
            { icon: "ri-mail-line", label: "Business Email", value: "hello@aidanbarrett.gg", color: "#e879f9" },
            { icon: "ri-twitch-fill", label: "Live on Twitch", value: "@AidanBarrettGG", color: "#9147ff" },
            { icon: "ri-discord-fill", label: "Discord Server", value: "Aidan&apos;s Lair", color: "#5865f2" },
          ].map((info) => (
            <div key={info.label} className="card-gaming rounded-xl px-5 py-4 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${info.color}15` }}>
                <i className={`${info.icon} text-lg`} style={{ color: info.color }} />
              </div>
              <div>
                <p className="font-mono-tech text-xs text-slate-500">{info.label}</p>
                <p className="font-rajdhani text-sm font-semibold text-white">{info.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Form */}
        <div
          className={`card-gaming rounded-2xl p-8 transition-all duration-700 delay-200 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {status === "success" ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="w-16 h-16 flex items-center justify-center rounded-full border-2 border-[#e879f9] bg-[#e879f9]/10">
                <i className="ri-check-line text-[#e879f9] text-2xl" />
              </div>
              <h3 className="font-orbitron text-lg font-bold text-white">Message Sent!</h3>
              <p className="font-rajdhani text-sm text-slate-400 text-center">
                Got it! I&apos;ll get back to you soon.
              </p>
              <button
                onClick={() => setStatus("idle")}
                className="font-orbitron text-xs uppercase tracking-widest px-6 py-2 rounded-md border border-[#e879f9]/40 text-[#e879f9] hover:bg-[#e879f9]/10 transition-all cursor-pointer whitespace-nowrap"
              >
                Send Another
              </button>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="font-mono-tech text-xs text-[#e879f9] uppercase tracking-wider block mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Your name..."
                    className="w-full bg-white/3 border border-white/10 rounded-md px-4 py-3 text-sm font-rajdhani text-white placeholder-slate-600 focus:outline-none focus:border-[#e879f9]/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="font-mono-tech text-xs text-[#e879f9] uppercase tracking-wider block mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="w-full bg-white/3 border border-white/10 rounded-md px-4 py-3 text-sm font-rajdhani text-white placeholder-slate-600 focus:outline-none focus:border-[#e879f9]/50 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="font-mono-tech text-xs text-[#e879f9] uppercase tracking-wider block mb-2">
                  Subject
                </label>
                <select
                  name="subject"
                  required
                  className="w-full bg-[#0e0c1e] border border-white/10 rounded-md px-4 py-3 text-sm font-rajdhani text-slate-300 focus:outline-none focus:border-[#e879f9]/50 transition-colors cursor-pointer"
                >
                  <option value="">Select a topic...</option>
                  <option value="Collaboration">Collaboration</option>
                  <option value="Sponsorship">Sponsorship / Brand Deal</option>
                  <option value="Media">Media &amp; Press</option>
                  <option value="Fan Mail">Fan Mail</option>
                  <option value="Business">Business Inquiry</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="font-mono-tech text-xs text-[#e879f9] uppercase tracking-wider block mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="Tell me what&apos;s up..."
                  className="w-full bg-white/3 border border-white/10 rounded-md px-4 py-3 text-sm font-rajdhani text-white placeholder-slate-600 focus:outline-none focus:border-[#e879f9]/50 transition-colors resize-none"
                />
              </div>

              {status === "error" && (
                <p className="font-mono-tech text-xs text-red-400">Something went wrong. Please try again.</p>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                className="btn-neon w-full py-3 rounded-md bg-[#e879f9]/10 border-2 border-[#e879f9] text-[#e879f9] font-orbitron text-sm font-bold uppercase tracking-widest hover:bg-[#e879f9]/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap animate-border-glow"
              >
                {status === "sending" ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span>
                    <i className="ri-send-plane-fill mr-2" />
                    Send Message
                  </span>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
